import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { CheckCircle, Mail } from 'lucide-react';
import Logo from '../components/Logo';
import LanguageSelector from '../components/LanguageSelector';
import { supabase } from '../lib/supabaseClient';
import { toast } from 'react-toastify';

const EmailVerification: React.FC = () => {
  const { t } = useTranslation();
  const [resending, setResending] = useState(false);
  const [email, setEmail] = useState(() => localStorage.getItem('pendingVerificationEmail') || '');

  const handleResendEmail = async () => {
    if (!email) {
      toast.error(t('emailRequired'));
      return;
    }

    try {
      setResending(true);
      
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/email-confirmed`
        }
      });
      
      if (error) throw error;
      
      toast.success(t('emailConfirmationSent'));
    } catch (error: any) {
      console.error('Error resending verification email:', error);
      toast.error(error.message || t('errorResendingEmail'));
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
      <div className="absolute top-4 right-4">
        <LanguageSelector />
      </div>
      
      <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8 text-center">
        <Logo size="large" className="mx-auto mb-6" />
        
        <div className="flex justify-center mb-6">
          <Mail className="h-16 w-16 text-blue-600" />
        </div>
        
        <h1 className="text-2xl font-bold text-gray-900 mb-4">
          {t('checkYourEmail')}
        </h1>
        
        <p className="text-gray-600 mb-6">
          {t('emailVerificationInstructions')}
        </p>
        
        <div className="space-y-4">
          <Link
            to="/login"
            className="block w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
          >
            {t('backToLogin')}
          </Link>
          
          <p className="text-sm text-gray-500">
            {t('didntReceiveEmail')}{' '}
            <button 
              onClick={handleResendEmail}
              disabled={resending}
              className="text-blue-600 hover:text-blue-800 focus:outline-none disabled:opacity-50"
            >
              {resending ? t('sending') : t('resendEmail')}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default EmailVerification;